export function findSelectedOption(question, answerKey) {
  if (!answerKey) {
    return null;
  }

  return question.options.find((option) => option.key === answerKey) || null;
}

export function getVisibleQuestions(family, answers = {}) {
  const visibleQuestions = [];
  let shouldStop = false;

  family.questions.forEach((question) => {
    if (shouldStop) {
      return;
    }

    visibleQuestions.push(question);

    if (!answers[question.key]) {
      shouldStop = true;
    }
  });

  return visibleQuestions;
}

export function questionIndex(family, questionKey) {
  return family.questions.findIndex((question) => question.key === questionKey);
}

export function optionQuestionMap(family) {
  return family.questions.reduce((map, question) => {
    question.options.forEach((option) => {
      map[option.key] = question.key;
    });

    return map;
  }, {});
}

export function selectedOrDefaultOptionKey(family, questionKey, answers = {}) {
  const question = family.questions.find((entry) => entry.key === questionKey) || null;

  if (!question) {
    return null;
  }

  return answers[questionKey] || question.options[0]?.key || null;
}

export function isMatrixFamily(family) {
  return Boolean(family.price_matrix && family.matrix_groups && family.matrix_groups.length === 2);
}

export function matrixBasePriceCents(family, answers = {}) {
  if (!isMatrixFamily(family)) {
    return family.gross_price_cents;
  }

  const [primaryGroup, secondaryGroup] = family.matrix_groups;
  const primaryKey = selectedOrDefaultOptionKey(family, primaryGroup, answers);
  const secondaryKey = selectedOrDefaultOptionKey(family, secondaryGroup, answers);

  return family.price_matrix?.[primaryKey]?.[secondaryKey] || family.gross_price_cents;
}

export function additiveOptionTotalCents(family, answers = {}) {
  return family.questions.reduce((sum, question) => {
    if (isMatrixFamily(family) && family.matrix_groups.includes(question.key)) {
      return sum;
    }

    const selectedOption = findSelectedOption(question, answers[question.key]);
    return sum + (selectedOption?.price_delta_cents || 0);
  }, 0);
}

export function getSelectedOptions(family, answers = {}) {
  return family.questions
    .map((question) => findSelectedOption(question, answers[question.key]))
    .filter((option) => option);
}

export function getSelectedOptionKeys(family, answers = {}) {
  return getSelectedOptions(family, answers).map((option) => option.key);
}

export function allQuestionsAnswered(family, answers = {}) {
  return family.questions.every((question) => answers[question.key]);
}

export function getFirstUnansweredQuestionIndex(family, answers = {}) {
  return family.questions.findIndex((question) => !answers[question.key]);
}

export function getMaxUnlockedWizardStepIndex(family, answers = {}, { skipWizardResultStep = false } = {}) {
  if (allQuestionsAnswered(family, answers)) {
    return skipWizardResultStep ? Math.max(family.questions.length - 1, 0) : family.questions.length;
  }

  const firstUnansweredQuestionIndex = getFirstUnansweredQuestionIndex(family, answers);

  if (firstUnansweredQuestionIndex === -1) {
    return Math.max(family.questions.length - 1, 0);
  }

  return firstUnansweredQuestionIndex;
}

export function formatProjectLicensePrice(cents, locale = 'de') {
  return new Intl.NumberFormat(locale === 'de' ? 'de-DE' : 'en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format((cents || 0) / 100);
}

export function roundPriceCentsToFiveCentStep(cents) {
  const normalizedCents = Number(cents || 0);

  if (normalizedCents <= 0) {
    return 0;
  }

  return Math.ceil(normalizedCents / 5) * 5;
}

export function getEstimatedPriceCents(family, answers = {}) {
  let rawTotalCents;

  if (isMatrixFamily(family)) {
    rawTotalCents = matrixBasePriceCents(family, answers) + additiveOptionTotalCents(family, answers);
    return roundPriceCentsToFiveCentStep(rawTotalCents);
  }

  const selectedOptions = getSelectedOptions(family, answers);
  const additiveTotal = selectedOptions.reduce((sum, option) => sum + (option.price_delta_cents || 0), family.gross_price_cents);
  const multiplier = selectedOptions.reduce((currentMultiplier, option) => {
    if (!option.price_multiplier) {
      return currentMultiplier;
    }

    return currentMultiplier * option.price_multiplier;
  }, 1.0);

  rawTotalCents = Math.round(additiveTotal * multiplier);

  return roundPriceCentsToFiveCentStep(rawTotalCents);
}

export function optionPriceLabel(family, question, option, answers = {}, locale = 'de') {
  if (isMatrixFamily(family) && family.matrix_groups.includes(question.key)) {
    const currentTotal = matrixBasePriceCents(family, answers);
    const nextAnswers = {
      ...answers,
      [question.key]: option.key
    };
    const nextTotal = matrixBasePriceCents(family, nextAnswers);
    const delta = nextTotal - currentTotal;

    if (delta > 0) {
      return `+ ${formatProjectLicensePrice(delta, locale)}`;
    }

    return null;
  }

  if (option.price_delta_cents > 0) {
    return `+ ${formatProjectLicensePrice(option.price_delta_cents, locale)}`;
  }

  if (option.price_multiplier && option.price_multiplier > 1) {
    return `× ${String(option.price_multiplier).replace('.', ',')}`;
  }

  return null;
}

export function normalizeScenario(scenario) {
  if (typeof scenario === 'string') {
    return { title: scenario };
  }

  return scenario || {};
}

const COMPATIBLE_OPTION_KEYS = {
  channel_scope_multi_channel: ['channel_scope_single_channel'],
  usage_relation_collab_on_own_channels: ['usage_relation_own_use'],
  marketing_intent_promotional: ['marketing_intent_non_promotional'],
  monetization_yes: ['monetization_no'],
  paid_budget_tier_250: ['paid_budget_tier_organic'],
  paid_budget_tier_2000: ['paid_budget_tier_organic', 'paid_budget_tier_250'],
  paid_budget_tier_10000: ['paid_budget_tier_organic', 'paid_budget_tier_250', 'paid_budget_tier_2000'],
  paid_budget_tier_50000: ['paid_budget_tier_organic', 'paid_budget_tier_250', 'paid_budget_tier_2000', 'paid_budget_tier_10000'],
  content_model_paid_product: ['content_model_standard'],
  format_branding_scope_series_intro_outro: ['format_branding_scope_none'],
  format_branding_scope_signature_branding: ['format_branding_scope_none', 'format_branding_scope_series_intro_outro'],
  usage_intensity_multi_use: ['usage_intensity_single_use'],
  territory_scope_one_country: ['territory_scope_local_broadcast'],
  territory_scope_dach: ['territory_scope_local_broadcast', 'territory_scope_one_country'],
  territory_scope_four_to_five_countries: ['territory_scope_local_broadcast', 'territory_scope_one_country', 'territory_scope_dach'],
  territory_scope_six_to_ten_countries: ['territory_scope_local_broadcast', 'territory_scope_one_country', 'territory_scope_dach', 'territory_scope_four_to_five_countries'],
  territory_scope_europe: ['territory_scope_local_broadcast', 'territory_scope_one_country', 'territory_scope_dach', 'territory_scope_four_to_five_countries', 'territory_scope_six_to_ten_countries'],
  territory_scope_global: ['territory_scope_local_broadcast', 'territory_scope_one_country', 'territory_scope_dach', 'territory_scope_four_to_five_countries', 'territory_scope_six_to_ten_countries', 'territory_scope_europe'],
  rights_term_6m: ['rights_term_3m'],
  rights_term_12m: ['rights_term_3m', 'rights_term_6m']
};

function selectedOrCompatibleOptionKeys(selectedOptionKeys = []) {
  return [...new Set(selectedOptionKeys.flatMap((key) => [key, ...(COMPATIBLE_OPTION_KEYS[key] || [])]))];
}

export function getCoverageScenarioStatus(family, rawScenario, answers = {}) {
  const scenario = normalizeScenario(rawScenario);
  const selectedOptionKeys = getSelectedOptionKeys(family, answers);
  const compatibleOptionKeys = selectedOrCompatibleOptionKeys(selectedOptionKeys);
  const questionMap = optionQuestionMap(family);
  const matchAll = scenario.match_all || [];
  const matchAny = scenario.match_any || [];
  const excludeAny = scenario.exclude_any || [];

  if (scenario.review) {
    return 'review';
  }

  if (excludeAny.some((key) => selectedOptionKeys.includes(key))) {
    return 'not_covered';
  }

  if (matchAll.length === 0 && matchAny.length === 0) {
    return 'covered';
  }

  const allMatched = matchAll.every((key) => compatibleOptionKeys.includes(key));
  const anyMatched = matchAny.length > 0 && matchAny.some((key) => compatibleOptionKeys.includes(key));

  const requiredQuestionsAnsweredWithDifferentOption = matchAll.some((key) => {
    const questionKey = questionMap[key];

    return questionKey && answers[questionKey] && !compatibleOptionKeys.includes(key);
  });

  const anyQuestions = [...new Set(matchAny.map((key) => questionMap[key]).filter((key) => key))];
  const allAnyQuestionsAnswered = anyQuestions.length > 0 && anyQuestions.every((questionKey) => answers[questionKey]);

  if (requiredQuestionsAnsweredWithDifferentOption) {
    return 'not_covered';
  }

  if (allAnyQuestionsAnswered && !anyMatched) {
    return 'not_covered';
  }

  if (allMatched && (matchAny.length === 0 || anyMatched)) {
    return 'covered';
  }

  return 'open';
}

export function buildConfigurationPayload(family, answers = {}) {
  return {
    familyKey: family.key,
    familyTitle: family.title,
    selectedOptionKeys: getSelectedOptionKeys(family, answers),
    answers: answers,
    estimatedPriceCents: getEstimatedPriceCents(family, answers)
  };
}
